import { type DateRange, getRange, toDateString } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeesAllRoute } from "../../app-routes";
import {
    absenceTimeEntriesQuery,
    absencesQuery,
    availabilitiesQuery,
    rosterNotesQuery,
    schoolHolidaysQuery,
    vacationBalancesQuery,
} from "./calendar.queries";
import type { EmployeesAllLayoutRoute } from "./employees-all.route";

const STALE_TIME = 30_000;

function validateDate(search: Record<string, unknown>): { date?: DateString } {
    return { date: search.date as DateString | undefined };
}

/**
 * The pages show a whole year and only scroll when the date moves within it, so the data depends on the year.
 * loaderDeps runs before beforeLoad's redirect, hence the fallback for a missing date.
 */
function yearOf({ date }: { date?: DateString }): DateRange {
    return getRange(date ?? new Date(), "year");
}

export function createAbsencesRoute(parent: EmployeesAllLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "absences",
        staticData: {
            title: "Abwesenheiten: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/abwesenheiten",
            menu: employeesAllRoute("absences"),
        },
        validateSearch: validateDate,
        beforeLoad: ({ search }): void => {
            if (!search.date) {
                redirectTo("/employees/all/absences", { ...search, date: toDateString(new Date()) });
            }
        },
        loaderDeps: ({ search }) => yearOf(search),
        loader: async ({ deps, context: { app, queryClient } }) => {
            // The balances are slow and only decorate the rows, so they don't block the navigation:
            // the page awaits the promise itself. Failures surface there.
            const vacationBalances = queryClient.fetchQuery({
                ...vacationBalancesQuery(app, deps),
                staleTime: STALE_TIME,
            });
            vacationBalances.catch(() => {});

            const [absences, entries, schoolHolidays, rosterNotes] = await Promise.all([
                queryClient.fetchQuery({ ...absencesQuery(app, deps), staleTime: STALE_TIME }),
                queryClient.fetchQuery({ ...absenceTimeEntriesQuery(app, deps), staleTime: STALE_TIME }),
                queryClient.fetchQuery({ ...schoolHolidaysQuery(app, deps), staleTime: STALE_TIME }),
                queryClient.fetchQuery({ ...rosterNotesQuery(app, deps), staleTime: STALE_TIME }),
            ]);
            return { range: deps, absences, entries, schoolHolidays, rosterNotes, vacationBalances };
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-absences-all
                class="fullbleed"
                .date=${match.search.date}
                .range=${match.loaderData?.range}
                .absences=${match.loaderData?.absences ?? []}
                .entries=${match.loaderData?.entries ?? []}
                .schoolHolidays=${match.loaderData?.schoolHolidays ?? []}
                .rosterNotes=${match.loaderData?.rosterNotes ?? []}
                .vacationBalances=${match.loaderData?.vacationBalances}
            ></ptc-employees-absences-all>`,
    }).lazy(lazyElement(() => import("./employees-absences-all")));
}

export function createAvailabilitiesRoute(parent: EmployeesAllLayoutRoute) {
    return createRoute({
        getParentRoute: () => parent,
        path: "availabilities",
        staticData: {
            title: "Verfügbarkeiten: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/verfuegbarkeiten",
            menu: employeesAllRoute("availabilities"),
        },
        validateSearch: validateDate,
        beforeLoad: ({ search }): void => {
            if (!search.date) {
                redirectTo("/employees/all/availabilities", { ...search, date: toDateString(new Date()) });
            }
        },
        loaderDeps: ({ search }) => yearOf(search),
        loader: async ({ deps, context: { app, queryClient } }) => {
            // The filtered employees are part of the key, not of loaderDeps: filter changes alone don't navigate.
            const employeeIds = app.getFilteredEmployees(deps).map((employee) => employee.id);
            const [availabilities, schoolHolidays, rosterNotes] = await Promise.all([
                queryClient.fetchQuery({ ...availabilitiesQuery(app, deps, employeeIds), staleTime: STALE_TIME }),
                queryClient.fetchQuery({ ...schoolHolidaysQuery(app, deps), staleTime: STALE_TIME }),
                queryClient.fetchQuery({ ...rosterNotesQuery(app, deps), staleTime: STALE_TIME }),
            ]);
            return { range: deps, availabilities, schoolHolidays, rosterNotes };
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-availabilities-all
                class="fullbleed"
                .date=${match.search.date}
                .range=${match.loaderData?.range}
                .availabilities=${match.loaderData?.availabilities ?? []}
                .schoolHolidays=${match.loaderData?.schoolHolidays ?? []}
                .rosterNotes=${match.loaderData?.rosterNotes ?? []}
            ></ptc-employees-availabilities-all>`,
    }).lazy(lazyElement(() => import("./employees-availabilities-all")));
}

export function createCalendarRoutes(parent: EmployeesAllLayoutRoute) {
    return [createAbsencesRoute(parent), createAvailabilitiesRoute(parent)] as const;
}
